import * as React from 'react';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// A small info icon that reveals an explanatory tooltip on hover/focus. Used to
// explain how a derived value (e.g. Transaction Volume) is calculated, next to
// a field label or a table column header. Multi-line hints render line-by-line
// (`whitespace-pre-line`). `toggleOnClick` additionally pins the tooltip open on a
// tap or click, for a surface that has ruled the hint must be reachable by touch.
export function InfoHint({
  hint,
  label,
  ariaLabel,
  className,
  toggleOnClick = false,
}: Readonly<{
  hint: string;
  label: string;
  ariaLabel?: string;
  className?: string;
  toggleOnClick?: boolean;
}>) {
  const [open, setOpen] = React.useState(false);
  const controlled = toggleOnClick ? { open, onOpenChange: setOpen } : {};

  return (
    <TooltipProvider>
      <Tooltip {...controlled}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel ?? `How ${label} is calculated`}
            className={cn('text-muted-foreground/70 hover:text-foreground', className)}
            {...(toggleOnClick
              ? {
                  onClick: () => {
                    setOpen((current) => !current);
                  },
                }
              : {})}
          >
            <Info className="size-3" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-line normal-case">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
